/**
 * @fileoverview Données mockées du backoffice GNPC.
 *
 * Frontend-only : ces objets dessinent les écrans et états UI en attendant les
 * endpoints `/admin/*`. Ils reprennent les noms du contrat OpenAPI pour éviter
 * un futur renommage massif lors du branchement backend.
 */

import { OPERATOR_LABELS } from '@gnpc/ui';
import type {
  ExportStatus,
  IntegrationStatus,
  OperatorCode,
  PaymentStatus,
  RefundStatus,
  SgiStatus,
} from '@gnpc/ui';

export type Capability =
  | 'dashboard:read'
  | 'payment:read'
  | 'payment:replay'
  | 'refund:request'
  | 'refund:approve'
  | 'reconciliation:read'
  | 'reconciliation:run'
  | 'export:create'
  | 'audit:read'
  | 'audit:export'
  | 'user:read'
  | 'user:invite'
  | 'user:disable'
  | 'roles:manage'
  | 'integrations:health';

export type BackofficeRole =
  | 'AGENT'
  | 'SUPERVISOR'
  | 'FINANCE_VALIDATOR'
  | 'AUDITOR'
  | 'ADMIN';

export interface CapabilityGroup {
  id: string;
  label: string;
  description: string;
  capabilities: Capability[];
}

export interface CurrentAgent {
  id: string;
  email: string;
  fullName: string;
  role: BackofficeRole;
  capabilities: Capability[];
  initials: string;
}

export interface DashboardMetrics {
  paymentsToday: number;
  amountCollectedToday: number;
  successRate24h: number;
  sgiPendingCount: number;
  sgiFailedCount: number;
  pendingRefundCount: number;
  exportsPending: number;
  /**
   * Taux d'enregistrement STI : part des paiements PAID dont toutes les
   * lignes sont RECORDED côté Quipux. C'est la santé de la chaîne AVAL —
   * le pendant du taux de succès financier (amont Paymetrust).
   */
  sgiRecordedRate: number;
  /**
   * Délai moyen (minutes) entre `payment_status=PAID` et l'enregistrement
   * STI complet. Une dérive de cette latence signale un connecteur qui
   * décroche AVANT que la file de rejeu n'explose.
   */
  avgSgiRecordDelayMinutes: number;
  /**
   * Latence d'enregistrement au 95ᵉ centile et au maximum (minutes). La
   * moyenne masque les traînes : p95 et max révèlent les paiements qui
   * décrochent réellement. Observation Quipux QFS (C-04).
   */
  sgiRecordDelayP95Minutes: number;
  sgiRecordDelayMaxMinutes: number;
  /**
   * Seuil (minutes) au-delà duquel la latence d'enregistrement bascule en
   * alerte sur la tuile de délai.
   */
  sgiRecordDelayAlertThresholdMinutes: number;
  /**
   * Montant (XOF) encaissé mais non encore enregistré chez Quipux : argent
   * immobilisé et à risque. Observation Quipux QFS (C-03) — afficher le
   * MONTANT, pas seulement le nombre.
   */
  sgiFailedAmount: number;
  /** Ancienneté du plus ancien encaissé non enregistré, ex. « 3 j ». */
  sgiFailedOldestAgeLabel: string;
  /** Montant total (XOF) des remboursements en attente d'approbation 4-eyes. */
  pendingRefundAmount: number;
  /**
   * Part des amendes payées avant leur échéance — mesure l'efficacité des
   * rappels SMS (FR-11) et du parcours citoyen.
   */
  paidBeforeDueRate: number;
}

export interface PaymentLineItem {
  liquidationNumber: string;
  plate: string;
  amount: number;
  sgiStatus: SgiStatus;
  /**
   * Code retour de l'API d'enregistrement Quipux quand la ligne est en échec
   * (ex. « Q-ERR-TIMEOUT »). Observation Quipux QFS (C-16) — afficher le
   * motif d'échec par ligne.
   */
  failureCode?: string;
  /** Libellé lisible du motif d'échec, présenté à l'agent (C-16). */
  failureReason?: string;
}

export interface AdminPayment {
  id: string;
  merchantReference: string;
  userPhone: string;
  paymentStatus: PaymentStatus;
  sgiStatus: SgiStatus;
  totalAmount: number;
  /**
   * Code opérateur OpenAPI (`operator_code`, nullable) — `null` tant que
   * l'usager n'a pas finalisé son choix sur le guichet Paymetrust. Le
   * libellé humain s'obtient via `OPERATOR_LABELS` de `@gnpc/ui`.
   */
  operatorCode: OperatorCode | null;
  plate: string;
  /**
   * Référence de la transaction chez l'opérateur mobile money (`null` si
   * l'usager n'a jamais atteint le guichet). Critère de recherche demandé
   * par Quipux (C-09).
   */
  operatorReference: string | null;
  /** N° de matricule du véhicule concerné — recherchable (C-09). */
  matricule: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Horodatage d'enregistrement complet chez Quipux (`null` tant que le
   * paiement n'est pas RECORDED). Alimente la colonne « Enregistré Quipux »
   * (C-11).
   */
  recordedAt: string | null;
  traceId: string;
  lineItems: PaymentLineItem[];
}

export interface RefundRequest {
  id: string;
  paymentId: string;
  requester: string;
  /** Agent ayant validé (approuvé ou rejeté) la demande — le « valideur » (C-20). */
  approver?: string;
  status: RefundStatus;
  amount: number;
  reason: string;
  /** Nom de la pièce justificative jointe à la demande (C-19). */
  documentName?: string;
  createdAt: string;
}

export interface AuditEntry {
  id: string;
  actorEmail: string;
  action: string;
  capability: Capability | null;
  targetType: string;
  targetId: string;
  ipAddress: string;
  createdAt: string;
  /** Montant (XOF) concerné — renseigné pour les actions financières (C-29). */
  amount?: number;
  changes: Record<string, unknown>;
}

export interface BackofficeUser {
  id: string;
  email: string;
  fullName: string;
  role: BackofficeRole;
  status: 'INVITED' | 'ACTIVE' | 'DISABLED';
  lastLoginAt?: string;
  capabilities: Capability[];
  activeSessions: number;
  /**
   * Double authentification activée. MFA obligatoire pour les rôles financiers
   * (observation Quipux QFS C-30) : un rôle financier sans MFA est signalé.
   */
  mfaEnabled: boolean;
}

export interface ExportJob {
  id: string;
  format: 'CSV' | 'XLSX' | 'PDF';
  status: ExportStatus;
  createdBy: string;
  createdAt: string;
  expiresAt?: string;
  scope: string;
  /** Motif d'échec du job, présenté à l'agent pour relance (C-26). */
  failureReason?: string;
  /** Récurrence d'un export planifié (ex. « Quotidien ») — C-25. */
  recurrence?: string;
}

export interface IntegrationHealth {
  id: string;
  label: string;
  status: IntegrationStatus;
  latencyMs: number;
  errorRate: number;
  lastCheckAt: string;
  description: string;
  /**
   * Impact métier d'un connecteur dégradé : nombre de transactions impactées.
   * Observation Quipux QFS (C-28) — vue orientée impact financier plutôt que
   * latence brute.
   */
  impactedCount?: number;
  /** Montant (XOF) immobilisé par la dégradation du connecteur (C-28). */
  impactedAmount?: number;
  /** Lien de drill-down vers les transactions impactées (C-28). */
  impactedLink?: string;
}

export interface ReconciliationBatch {
  id: string;
  period: string;
  matchedAmount: number;
  unmatchedAmount: number;
  matchedCount: number;
  unmatchedCount: number;
  status: 'OK' | 'REVIEW_REQUIRED';
  generatedAt: string;
}

export const ALL_CAPABILITIES: Capability[] = [
  'dashboard:read',
  'payment:read',
  'payment:replay',
  'refund:request',
  'refund:approve',
  'reconciliation:read',
  'reconciliation:run',
  'export:create',
  'audit:read',
  'audit:export',
  'user:read',
  'user:invite',
  'user:disable',
  'roles:manage',
  'integrations:health',
];

export const BACKOFFICE_ROLES: BackofficeRole[] = [
  'AGENT',
  'SUPERVISOR',
  'FINANCE_VALIDATOR',
  'AUDITOR',
  'ADMIN',
];

export const ROLE_LABELS: Record<BackofficeRole, string> = {
  AGENT: 'Agent',
  SUPERVISOR: 'Superviseur',
  FINANCE_VALIDATOR: 'Validateur financier QFS',
  AUDITOR: 'Auditeur',
  ADMIN: 'Administrateur',
};

export const ROLE_DESCRIPTIONS: Record<BackofficeRole, string> = {
  AGENT: 'Consultation opérationnelle des paiements et du tableau de bord.',
  SUPERVISOR: 'Traitement des anomalies, remboursements et rapprochements.',
  FINANCE_VALIDATOR:
    'Validation financière QFS : approbation des remboursements et clôture des rapprochements, sans accès à l’administration technique.',
  AUDITOR: 'Consultation, contrôle et export des traces sans action de mutation.',
  ADMIN:
    'Administration technique, sécurité et accès. Ne cumule pas la validation financière (séparation des pouvoirs).',
};

export const CAPABILITY_LABELS: Record<Capability, string> = {
  'dashboard:read': 'Voir le tableau de bord',
  'payment:read': 'Consulter les paiements',
  'payment:replay': 'Rejouer un enregistrement Quipux',
  'refund:request': 'Demander un remboursement',
  'refund:approve': 'Approuver un remboursement',
  'reconciliation:read': 'Lire les rapprochements',
  'reconciliation:run': 'Lancer un rapprochement',
  'export:create': 'Créer des exports',
  'audit:read': "Lire le journal d'audit",
  'audit:export': "Exporter le journal d'audit",
  'user:read': 'Consulter les agents',
  'user:invite': 'Inviter un agent',
  'user:disable': 'Désactiver un agent',
  'roles:manage': 'Gérer les rôles et permissions',
  'integrations:health': 'Voir la santé des connecteurs',
};

export const CAPABILITY_GROUPS: CapabilityGroup[] = [
  {
    id: 'overview',
    label: 'Vue et consultation',
    description: 'Accès aux écrans de suivi et aux données de paiement.',
    capabilities: ['dashboard:read', 'payment:read', 'reconciliation:read'],
  },
  {
    id: 'operations',
    label: 'Traitement opérationnel',
    description: 'Actions qui modifient ou relancent un traitement.',
    capabilities: ['payment:replay', 'refund:request', 'refund:approve'],
  },
  {
    id: 'supervision',
    label: 'Supervision et audit',
    description: 'Contrôle des connecteurs, du journal et des exports.',
    capabilities: [
      'integrations:health',
      'reconciliation:run',
      'audit:read',
      'audit:export',
      'export:create',
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    description: 'Gestion des agents et de leurs accès.',
    capabilities: ['user:read', 'user:invite', 'user:disable', 'roles:manage'],
  },
];

export const ROLE_CAPABILITIES: Record<BackofficeRole, Capability[]> = {
  AGENT: ['dashboard:read', 'payment:read'],
  SUPERVISOR: [
    'dashboard:read',
    'payment:read',
    'payment:replay',
    'refund:request',
    'reconciliation:read',
    'reconciliation:run',
    'export:create',
    'integrations:health',
  ],
  // Rôle dédié « Validateur financier QFS » (C-32) : porte la validation
  // financière (approbation des remboursements, rapprochement) séparément de
  // l'administration technique — matérialise la séparation des pouvoirs (C-31).
  FINANCE_VALIDATOR: [
    'dashboard:read',
    'payment:read',
    'refund:approve',
    'reconciliation:read',
    'reconciliation:run',
    'audit:read',
  ],
  AUDITOR: ['dashboard:read', 'payment:read', 'audit:read', 'audit:export', 'export:create'],
  // Séparation des pouvoirs (C-31) : l'Administrateur gère la technique, la
  // sécurité et les accès, mais NE cumule PAS la validation financière —
  // `refund:approve` est réservé au « Validateur financier QFS ».
  ADMIN: ALL_CAPABILITIES.filter((capability) => capability !== 'refund:approve'),
};

export const CURRENT_AGENT: CurrentAgent = {
  id: 'usr_admin_aicha',
  email: 'aicha.diomande@quipux.ci',
  fullName: 'Aïcha Diomandé',
  role: 'ADMIN',
  capabilities: ALL_CAPABILITIES,
  initials: 'AD',
};

export const DASHBOARD_METRICS: DashboardMetrics = {
  paymentsToday: 4127,
  amountCollectedToday: 84200000,
  successRate24h: 98.4,
  sgiPendingCount: 18,
  sgiFailedCount: 7,
  pendingRefundCount: 5,
  exportsPending: 2,
  sgiRecordedRate: 96.2,
  avgSgiRecordDelayMinutes: 4,
  sgiRecordDelayP95Minutes: 9,
  sgiRecordDelayMaxMinutes: 23,
  sgiRecordDelayAlertThresholdMinutes: 15,
  sgiFailedAmount: 168000,
  sgiFailedOldestAgeLabel: '3 j',
  pendingRefundAmount: 386000,
  paidBeforeDueRate: 71.5,
};

export const ADMIN_PAYMENTS: AdminPayment[] = [
  {
    id: 'pay_01J2A5X8Y6M8C4K9GNPC001',
    merchantReference: '01J2A5X8Y6M8C4K9GNPC001',
    userPhone: '+2250700000042',
    paymentStatus: 'PAID',
    sgiStatus: 'FAILED',
    totalAmount: 117000,
    operatorCode: 'OM_CI',
    plate: 'AB 572 GG',
    createdAt: '2026-07-14T08:22:00Z',
    updatedAt: '2026-07-14T08:27:00Z',
    operatorReference: 'OM-TX-8830021',
    matricule: 'MAT-1180237',
    recordedAt: null,
    traceId: 'trc_7cae2f8d1b7a',
    lineItems: [
      {
        liquidationNumber: 'LIQ-921555',
        plate: 'AB 572 GG',
        amount: 45000,
        sgiStatus: 'FAILED',
        failureCode: 'Q-ERR-TIMEOUT',
        failureReason: 'Délai dépassé côté Quipux : aucune réponse de l’API d’enregistrement.',
      },
      { liquidationNumber: 'LIQ-921556', plate: 'AB 572 GG', amount: 12000, sgiStatus: 'RECORDED' },
      {
        liquidationNumber: 'LIQ-921557',
        plate: 'AB 572 GG',
        amount: 60000,
        sgiStatus: 'FAILED',
        failureCode: 'Q-ERR-REF-INCONNUE',
        failureReason: 'Code de paiement inconnu côté Quipux.',
      },
    ],
  },
  {
    id: 'pay_01J2A5X8Y6M8C4K9GNPC002',
    merchantReference: '01J2A5X8Y6M8C4K9GNPC002',
    userPhone: '+2250500003311',
    paymentStatus: 'PAID',
    sgiStatus: 'RECORDED',
    totalAmount: 45000,
    operatorCode: 'MTN_CI',
    plate: 'DK 418 HA',
    createdAt: '2026-07-14T07:48:00Z',
    updatedAt: '2026-07-14T07:51:00Z',
    operatorReference: 'MTN-9920145',
    matricule: 'MAT-2040911',
    recordedAt: '2026-07-14T07:51:00Z',
    traceId: 'trc_25d9f075c402',
    lineItems: [
      { liquidationNumber: 'LIQ-921610', plate: 'DK 418 HA', amount: 45000, sgiStatus: 'RECORDED' },
    ],
  },
  {
    id: 'pay_01J2A5X8Y6M8C4K9GNPC003',
    merchantReference: '01J2A5X8Y6M8C4K9GNPC003',
    userPhone: '+2250100007812',
    paymentStatus: 'PENDING',
    sgiStatus: 'NOT_APPLICABLE',
    totalAmount: 12000,
    operatorCode: null,
    plate: 'CI 009 ZE',
    createdAt: '2026-07-14T07:30:00Z',
    updatedAt: '2026-07-14T07:30:00Z',
    operatorReference: null,
    matricule: 'MAT-3387120',
    recordedAt: null,
    traceId: 'trc_89452b05a112',
    lineItems: [
      { liquidationNumber: 'LIQ-921611', plate: 'CI 009 ZE', amount: 12000, sgiStatus: 'NOT_APPLICABLE' },
    ],
  },
  {
    id: 'pay_01J2A5X8Y6M8C4K9GNPC004',
    merchantReference: '01J2A5X8Y6M8C4K9GNPC004',
    userPhone: '+2250700006655',
    paymentStatus: 'PAID',
    sgiStatus: 'PARTIAL',
    totalAmount: 90000,
    operatorCode: 'MOOV_CI',
    plate: 'LT 312 QS',
    createdAt: '2026-07-13T18:11:00Z',
    updatedAt: '2026-07-13T18:18:00Z',
    operatorReference: 'MOOV-5521033',
    matricule: 'MAT-4419002',
    recordedAt: null,
    traceId: 'trc_b17d3e6fd923',
    lineItems: [
      { liquidationNumber: 'LIQ-921612', plate: 'LT 312 QS', amount: 30000, sgiStatus: 'RECORDED' },
      { liquidationNumber: 'LIQ-921613', plate: 'LT 312 QS', amount: 60000, sgiStatus: 'PENDING' },
    ],
  },
  {
    id: 'pay_01J2A5X8Y6M8C4K9GNPC005',
    merchantReference: '01J2A5X8Y6M8C4K9GNPC005',
    userPhone: '+2250700007721',
    paymentStatus: 'FAILED',
    sgiStatus: 'NOT_APPLICABLE',
    totalAmount: 60000,
    operatorCode: 'WAVE_CI',
    plate: 'AG 204 KN',
    createdAt: '2026-07-13T16:04:00Z',
    updatedAt: '2026-07-13T16:06:00Z',
    operatorReference: 'WAVE-7741882',
    matricule: 'MAT-5502288',
    recordedAt: null,
    traceId: 'trc_31a7e947cc20',
    lineItems: [
      { liquidationNumber: 'LIQ-921614', plate: 'AG 204 KN', amount: 60000, sgiStatus: 'NOT_APPLICABLE' },
    ],
  },
];

export const REFUNDS: RefundRequest[] = [
  {
    id: 'ref_001',
    paymentId: 'pay_01J2A5X8Y6M8C4K9GNPC001',
    requester: 'moussa.kone@quipux.ci',
    status: 'REQUESTED',
    amount: 60000,
    reason: 'Enregistrement Quipux définitivement échoué sur une ligne.',
    createdAt: '2026-07-14T09:10:00Z',
  },
  {
    id: 'ref_002',
    paymentId: 'pay_01J2A5X8Y6M8C4K9GNPC004',
    requester: 'fatou.kouame@quipux.ci',
    approver: 'aicha.diomande@quipux.ci',
    status: 'APPROVED',
    amount: 60000,
    reason: 'Ligne partielle à rembourser après analyse superviseur.',
    documentName: 'analyse-superviseur.pdf',
    createdAt: '2026-07-13T19:05:00Z',
  },
  /**
   * Demande portée par l'agent courant (`CURRENT_AGENT`) : elle rend la
   * règle des 4 yeux VISIBLE dans la maquette — le bouton d'approbation
   * est désactivé car demandeur = approbateur potentiel.
   */
  {
    id: 'ref_003',
    paymentId: 'pay_01J2A5X8Y6M8C4K9GNPC001',
    requester: 'aicha.diomande@quipux.ci',
    status: 'REQUESTED',
    amount: 45000,
    reason: 'Seconde ligne d’enregistrement Quipux en échec définitif (LIQ-921555).',
    createdAt: '2026-07-14T10:32:00Z',
  },
  {
    id: 'ref_004',
    paymentId: 'pay_01J2A5X8Y6M8C4K9GNPC004',
    requester: 'moussa.kone@quipux.ci',
    approver: 'aicha.diomande@quipux.ci',
    status: 'REJECTED',
    amount: 30000,
    reason: 'Demande non justifiée : la ligne a finalement été enregistrée chez Quipux.',
    documentName: 'controle-superviseur.pdf',
    createdAt: '2026-07-13T20:15:00Z',
  },
];

export const AUDIT_LOG: AuditEntry[] = [
  {
    id: 'aud_001',
    actorEmail: 'aicha.diomande@quipux.ci',
    action: 'payment.replay.requested',
    capability: 'payment:replay',
    targetType: 'payment',
    targetId: 'pay_01J2A5X8Y6M8C4K9GNPC001',
    ipAddress: '196.49.12.44',
    createdAt: '2026-07-14T09:18:00Z',
    changes: { reason: 'Timeout Quipux après débit confirmé', lineCount: 2 },
  },
  {
    id: 'aud_002',
    actorEmail: 'moussa.kone@quipux.ci',
    action: 'refund.requested',
    capability: 'refund:request',
    targetType: 'refund',
    targetId: 'ref_001',
    ipAddress: '196.49.12.45',
    createdAt: '2026-07-14T09:10:00Z',
    amount: 60000,
    changes: { amount: 60000, paymentId: 'pay_01J2A5X8Y6M8C4K9GNPC001' },
  },
  {
    id: 'aud_003',
    actorEmail: 'system@quipux.ci',
    action: 'integration.health.changed',
    capability: null,
    targetType: 'integration',
    targetId: 'sti',
    ipAddress: '10.0.4.12',
    createdAt: '2026-07-14T08:50:00Z',
    changes: { from: 'UP', to: 'DEGRADED', latencyMs: 1450 },
  },
  {
    id: 'aud_004',
    actorEmail: 'aicha.diomande@quipux.ci',
    action: 'refund.approved',
    capability: 'refund:approve',
    targetType: 'refund',
    targetId: 'ref_002',
    ipAddress: '196.49.12.44',
    createdAt: '2026-07-13T19:20:00Z',
    amount: 60000,
    changes: { approver: 'aicha.diomande@quipux.ci', paymentId: 'pay_01J2A5X8Y6M8C4K9GNPC004' },
  },
  {
    id: 'aud_005',
    actorEmail: 'fatou.kouame@quipux.ci',
    action: 'reconciliation.discrepancy.closed',
    capability: 'reconciliation:run',
    targetType: 'reconciliation',
    targetId: 'rec_2026-07-14',
    ipAddress: '196.49.12.45',
    createdAt: '2026-07-14T11:05:00Z',
    amount: 168000,
    changes: { annotation: 'Écart justifié : lignes en rejeu Quipux', batchId: 'rec_2026-07-14' },
  },
];

export const USERS: BackofficeUser[] = [
  {
    id: 'usr_001',
    email: 'aicha.diomande@quipux.ci',
    fullName: 'Aïcha Diomandé',
    role: 'ADMIN',
    status: 'ACTIVE',
    lastLoginAt: '2026-07-14T08:15:00Z',
    capabilities: ALL_CAPABILITIES,
    activeSessions: 1,
    mfaEnabled: true,
  },
  {
    id: 'usr_002',
    email: 'moussa.kone@quipux.ci',
    fullName: 'Moussa Koné',
    role: 'SUPERVISOR',
    status: 'ACTIVE',
    lastLoginAt: '2026-07-14T07:20:00Z',
    capabilities: ['dashboard:read', 'payment:read', 'payment:replay', 'refund:request', 'export:create'],
    activeSessions: 2,
    mfaEnabled: true,
  },
  {
    id: 'usr_003',
    email: 'fatou.kouame@quipux.ci',
    fullName: 'Fatou Kouamé',
    role: 'AUDITOR',
    status: 'ACTIVE',
    lastLoginAt: '2026-07-13T15:40:00Z',
    capabilities: ['dashboard:read', 'payment:read', 'audit:read', 'audit:export', 'export:create'],
    activeSessions: 1,
    mfaEnabled: false,
  },
  {
    id: 'usr_004',
    email: 'invite.agent@quipux.ci',
    fullName: 'Nouvel agent',
    role: 'AGENT',
    status: 'INVITED',
    capabilities: ['dashboard:read', 'payment:read'],
    activeSessions: 0,
    mfaEnabled: false,
  },
  {
    id: 'usr_005',
    email: 'koffi.brou@quipux.ci',
    fullName: 'Koffi Brou',
    role: 'FINANCE_VALIDATOR',
    status: 'ACTIVE',
    lastLoginAt: '2026-07-14T06:50:00Z',
    capabilities: [
      'dashboard:read',
      'payment:read',
      'refund:approve',
      'reconciliation:read',
      'reconciliation:run',
      'audit:read',
    ],
    activeSessions: 1,
    // Rôle financier SANS MFA — déclenche l'alerte « MFA requise » (C-30).
    mfaEnabled: false,
  },
];

export const EXPORT_JOBS: ExportJob[] = [
  {
    id: 'exp_001',
    format: 'XLSX',
    status: 'PENDING',
    createdBy: 'aicha.diomande@quipux.ci',
    createdAt: '2026-07-14T09:22:00Z',
    scope: 'Paiements · 14 juillet · PAID + échec Quipux',
  },
  {
    id: 'exp_002',
    format: 'CSV',
    status: 'COMPLETED',
    createdBy: 'fatou.kouame@quipux.ci',
    createdAt: '2026-07-13T17:05:00Z',
    expiresAt: '2026-07-20T17:05:00Z',
    scope: 'Audit · juillet 2026',
  },
  {
    id: 'exp_003',
    format: 'PDF',
    status: 'FAILED',
    createdBy: 'moussa.kone@quipux.ci',
    createdAt: '2026-07-13T10:35:00Z',
    scope: 'Rapprochement · semaine 28',
    failureReason: 'Source indisponible : le service de rapprochement n’a pas répondu (délai dépassé).',
  },
  {
    id: 'exp_004',
    format: 'XLSX',
    status: 'COMPLETED',
    createdBy: 'aicha.diomande@quipux.ci',
    createdAt: '2026-07-14T06:00:00Z',
    expiresAt: '2026-07-21T06:00:00Z',
    scope: 'Rapprochement quotidien · automatique',
    recurrence: 'Quotidien',
  },
];

export const INTEGRATIONS: IntegrationHealth[] = [
  {
    id: 'sti',
    label: 'Plateforme Quipux',
    status: 'DEGRADED',
    latencyMs: 1450,
    errorRate: 3.8,
    lastCheckAt: '2026-07-14T09:25:00Z',
    description: 'Consultation disponible, écritures en rejeu sur certaines lignes.',
    impactedCount: DASHBOARD_METRICS.sgiFailedCount,
    impactedAmount: DASHBOARD_METRICS.sgiFailedAmount,
    impactedLink: '/payments?sgi_status=FAILED',
  },
  {
    id: 'paymetrust',
    label: 'Guichet paiement',
    status: 'UP',
    latencyMs: 280,
    errorRate: 0.1,
    lastCheckAt: '2026-07-14T09:25:00Z',
    description: 'Initiation et statut des paiements opérationnels.',
  },
];

export const RECONCILIATION_BATCHES: ReconciliationBatch[] = [
  {
    id: 'rec_2026_07_14',
    period: '14 juillet 2026',
    matchedAmount: 82450000,
    unmatchedAmount: 117000,
    matchedCount: 3984,
    unmatchedCount: 3,
    status: 'REVIEW_REQUIRED',
    generatedAt: '2026-07-14T08:00:00Z',
  },
  {
    id: 'rec_2026_07_13',
    period: '13 juillet 2026',
    matchedAmount: 79120000,
    unmatchedAmount: 0,
    matchedCount: 3810,
    unmatchedCount: 0,
    status: 'OK',
    generatedAt: '2026-07-13T08:00:00Z',
  },
];

export function maskPhone(phone: string): string {
  return phone.replace(/^(\+225)(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/, '$1 $2 XX XX XX $6');
}

/**
 * Libellé humain d'un code opérateur, avec repli pour les transactions où
 * l'usager n'a pas encore choisi son opérateur au guichet (`null`).
 * Préférer `<OperatorLogo withLabel />` quand le contexte permet un logo.
 */
export function operatorLabel(code: OperatorCode | null): string {
  return code ? OPERATOR_LABELS[code] : 'Opérateur non renseigné';
}

export function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
